/**
 * Donor Display Helpers
 *
 * Centralized utilities for displaying donor identifiers in the UI.
 * Uses a consistent fallback hierarchy to avoid exposing internal UUIDs:
 *
 * 1. crm_ref (e.g., "EFA-M001") - Human-readable CRM reference
 * 2. donor_number (e.g., 1001) - Numeric donor ID from CRM
 * 3. external_id (e.g., "DLC-77abc") - Source-system identifier (no prefix)
 * 4. Short UUID suffix - Last resort, only for internal debugging
 *
 * Benefits:
 * - Never accidentally show full UUIDs to end users
 * - Single source of truth for donor ID display logic
 * - Consistent across all UI components
 */

import type { Json } from "@/types/database.types";

/**
 * Custom fields shape for donors with CRM references.
 */
export interface DonorCustomFields {
  crm_ref?: string;
  donor_number?: number;
  external_id?: string;
  [key: string]: unknown;
}

/**
 * Minimal donor shape required for display ID resolution.
 */
export interface DonorForDisplay {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  custom_fields?: Json | null;
}

/**
 * Get a human-readable display ID for a donor.
 *
 * Fallback hierarchy:
 * 1. custom_fields.crm_ref (e.g., "EFA-M001")
 * 2. custom_fields.donor_number formatted (e.g., "#1001")
 * 3. custom_fields.external_id (e.g., "DLC-77abc") - source-system ID, no prefix
 * 4. Short UUID suffix (e.g., "...a1b2") - internal only
 *
 * @param donor - Donor object with id and optional custom_fields
 * @param options - Display options
 * @returns Human-readable identifier string
 */
export function getDonorDisplayId(
  donor: DonorForDisplay,
  options: {
    /** If true, returns null instead of UUID fallback */
    preferNull?: boolean;
    /** Prefix for donor_number fallback (default: "#") */
    numberPrefix?: string;
    /** How many chars of UUID to show in fallback (default: 4) */
    uuidSuffixLength?: number;
  } = {},
): string | null {
  const {
    preferNull = false,
    numberPrefix = "#",
    uuidSuffixLength = 4,
  } = options;

  // Try to extract custom fields
  const customFields = extractCustomFields(donor.custom_fields);

  // Priority 1: CRM reference (e.g., "EFA-M001")
  if (customFields?.crm_ref && typeof customFields.crm_ref === "string") {
    return customFields.crm_ref;
  }

  // Priority 2: Donor number (e.g., "#1001")
  if (
    customFields?.donor_number !== undefined &&
    customFields.donor_number !== null
  ) {
    const num = Number(customFields.donor_number);
    if (!isNaN(num)) {
      return `${numberPrefix}${num}`;
    }
  }

  // Priority 3: External ID (source-system identifier, e.g., "DLC-77abc")
  if (
    typeof customFields?.external_id === "string" &&
    customFields.external_id.length > 0
  ) {
    return customFields.external_id;
  }

  // Priority 4: Short UUID suffix or null
  if (preferNull) {
    return null;
  }

  // Return short UUID suffix as last resort
  return `...${donor.id.slice(-uuidSuffixLength)}`;
}

/**
 * Get a human-readable display name for a donor.
 * Prefers first_name + last_name if available.
 * Falls back to donorIdentityResolver ref (CRM ref → donor number → UUID).
 *
 * When the CRM adapter ships, this function will fetch names from the CRM
 * instead of local fields — making this the single migration point.
 */
export function getDonorDisplayName(donor: DonorForDisplay): string {
  const name = [donor.first_name, donor.last_name].filter(Boolean).join(" ");
  return name || (getDonorDisplayId(donor) ?? donor.id);
}

/**
 * Get the CRM reference for a donor, or null if not set.
 */
export function getDonorCrmRef(donor: DonorForDisplay): string | null {
  const customFields = extractCustomFields(donor.custom_fields);
  if (customFields?.crm_ref && typeof customFields.crm_ref === "string") {
    return customFields.crm_ref;
  }
  return null;
}

/**
 * Get the donor number for a donor, or null if not set.
 */
export function getDonorNumber(donor: DonorForDisplay): number | null {
  const customFields = extractCustomFields(donor.custom_fields);
  if (
    customFields?.donor_number !== undefined &&
    customFields.donor_number !== null
  ) {
    const num = Number(customFields.donor_number);
    if (!isNaN(num)) {
      return num;
    }
  }
  return null;
}

/**
 * Check if a donor has a CRM reference set.
 */
export function hasCrmRef(donor: DonorForDisplay): boolean {
  return getDonorCrmRef(donor) !== null;
}

/**
 * Format a donor ID badge for display.
 * Returns an object with display text and whether it's a "real" ID vs UUID fallback.
 */
export function formatDonorIdBadge(donor: DonorForDisplay): {
  text: string;
  isRealId: boolean;
  type: "crm_ref" | "donor_number" | "external_id" | "uuid_suffix";
} {
  const customFields = extractCustomFields(donor.custom_fields);

  if (customFields?.crm_ref && typeof customFields.crm_ref === "string") {
    return {
      text: customFields.crm_ref,
      isRealId: true,
      type: "crm_ref",
    };
  }

  if (
    customFields?.donor_number !== undefined &&
    customFields.donor_number !== null
  ) {
    const num = Number(customFields.donor_number);
    if (!isNaN(num)) {
      return {
        text: `#${num}`,
        isRealId: true,
        type: "donor_number",
      };
    }
  }

  if (
    typeof customFields?.external_id === "string" &&
    customFields.external_id.length > 0
  ) {
    return {
      text: customFields.external_id,
      isRealId: true,
      type: "external_id",
    };
  }

  return {
    text: `...${donor.id.slice(-4)}`,
    isRealId: false,
    type: "uuid_suffix",
  };
}

/**
 * Safely extract custom_fields as a typed object.
 */
function extractCustomFields(
  customFields: Json | null | undefined,
): DonorCustomFields | null {
  if (!customFields) return null;
  if (typeof customFields !== "object") return null;
  if (Array.isArray(customFields)) return null;
  return customFields as DonorCustomFields;
}
